import type {
  Currency,
  ExpenseStatus,
  InvoiceStatus,
  PaymentStatus,
  RefundStatus,
} from '@duvamarine/shared';
import { INTL_LOCALES, useAppLocale } from '../../date-locales';
import { formatAmount } from '../../money';

/* Finance display helpers: status tones, Istanbul dates and money formatting. */

export type Tone = 'gold' | 'info' | 'success' | 'neutral' | 'warning' | 'danger';

export const PAYMENT_TONE: Record<PaymentStatus, Tone> = {
  PENDING: 'neutral',
  REQUIRES_ACTION: 'warning',
  AUTHORIZED: 'info',
  SUCCEEDED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  REFUNDED: 'neutral',
  PARTIALLY_REFUNDED: 'warning',
};

export const REFUND_TONE: Record<RefundStatus, Tone> = {
  PENDING: 'warning',
  SUCCEEDED: 'success',
  FAILED: 'danger',
};

export const INVOICE_TONE: Record<InvoiceStatus, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  SENT: 'info',
  PAID: 'success',
  PARTIALLY_PAID: 'warning',
  CANCELLED: 'danger',
};

export const EXPENSE_TONE: Record<ExpenseStatus, Tone> = {
  PENDING: 'warning',
  PAID: 'success',
  CANCELLED: 'neutral',
};

const TZ = 'Europe/Istanbul';

/** Today in Istanbul (YYYY-MM-DD). */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}

export function monthStartIso(): string {
  return `${todayIso().slice(0, 7)}-01`;
}

export function useDates() {
  const locale = useAppLocale();
  const intl = INTL_LOCALES[locale];
  const dateTime = new Intl.DateTimeFormat(intl, {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const instantDate = new Intl.DateTimeFormat(intl, {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const calendarDate = new Intl.DateTimeFormat(intl, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return {
    dateTime: (iso: string) => dateTime.format(new Date(iso)),
    /** A calendar day ("2026-10-21") or the Istanbul day of an instant. */
    date: (iso: string) =>
      iso.length === 10
        ? calendarDate.format(new Date(`${iso}T12:00:00Z`))
        : instantDate.format(new Date(iso)),
  };
}

export function useMoney() {
  const locale = useAppLocale();
  return {
    tr: (amountMinor: number) => formatAmount(amountMinor, 'TRY', locale),
    of: (amountMinor: number, currency: Currency) => formatAmount(amountMinor, currency, locale),
  };
}
